// Translated texts for the structured errors of /api/accounting/invoicing. The server sends
// English messages with raw cents; the UI keys them by `code` (and `field` for the Pflichtangaben
// checklist) so every language gets a readable sentence with formatted amounts.
import { formatCents } from "../../../serviceProvider/components/accounting/shared/money";
import { errorInfo } from "../../../serviceProvider/components/accounting/receivables/api";
import { isAuthenticated } from "../../Auth/auth";
import { checkSpecificPermission } from "../../../utils/checkPermissions";

/** May the current user use the accounting module with this action (Read, Create, Update)? */
export const canAccounting = action =>
  checkSpecificPermission(isAuthenticated()?.roleId?.permissions, "Accounting", action);

const finalNumberOf = message => (/Schlussrechnung\s+(\S+?)\./.exec(message || "") || [])[1] || "";

/** Readable, translated text of an invoicing API error. */
export const invoiceErrorText = (ta, error) => {
  const { message, code, details } = errorInfo(error);
  switch (code) {
    case "HAS_CORRECTIONS":
      return ta(
        "This invoice has invoice corrections. Cancel the corrections first, then cancel the invoice."
      );
    case "DEDUCTED_IN_FINAL":
      return ta(
        "This invoice is deducted in final invoice {{number}}. Cancel or correct the final invoice instead.",
        { number: finalNumberOf(message) }
      );
    case "WRITTEN_OFF":
      return ta("This invoice was written off. It can no longer be cancelled or corrected.");
    case "ALREADY_CANCELLED":
      return ta("This invoice is already cancelled.");
    case "STORNO_DATE":
      return ta("The cancellation date cannot be before the invoice date.");
    case "PERIOD_LOCKED":
      return ta("The date lies in a closed period. Choose a date in an open period.");
    case "NOT_SENT":
      return ta("This only works for a sent invoice.");
    case "CREDIT_USED":
      return ta("The credit from this invoice correction was already offset or refunded.");
    case "NOT_CANCELLABLE":
      return ta("A correction notice has no amounts and cannot be cancelled; issue a new one.");
    case "REASON_REQUIRED":
      return ta("An invoice correction needs a reason.");
    case "NOTHING_LEFT":
      return ta("Nothing is left to credit on this invoice.");
    case "ADVANCE_NOT_CORRECTABLE":
      return ta(
        "An advance invoice cannot be corrected. Cancel it and issue a new one, or correct the final invoice."
      );
    case "NOT_CORRECTABLE":
      return ta("Corrections, cancellations and correction notices cannot be corrected.");
    case "CREDIT_EXCEEDS_INVOICE":
      return ta("The invoice correction exceeds what is left of the invoice.");
    case "DIFFERENCE_TOO_LARGE":
      return ta("The payment difference is above the limit. Create an invoice correction instead.");
    case "AMOUNT":
      return ta("The amount must be greater than zero and at most the open amount.");
    case "NO_CHARGES":
      return ta("There are no open dunning charges.");
    case "BANK_PAYMENT":
      return ta(
        "This payment came from the bank. Match the returned bank transaction to the invoice instead."
      );
    case "ALREADY_REVERSED":
      return ta("This payment was already reversed.");
    case "NOT_REVERSIBLE":
      return ta("Only a received invoice payment can be reversed.");
    case "NO_FIELDS":
      return ta("Name at least one corrected field.");
    case "NO_CUSTOMER":
      return ta("The invoice needs a customer before it can be sent.");
    case "ZERO_TOTAL":
      return ta("The invoice total is zero.");
    case "NO_RELATED":
      return ta("A final invoice needs at least one sent advance or partial invoice.");
    case "TAX_MODE":
      return ta("Reverse charge needs a § 13b recipient or a customer VAT ID.");
    case "MISSING_MANDATORY":
      return ta("Required invoice details are missing.");
    case "NOT_OPEN":
      return ta("This document has no receivable to write off.");
    case "BRANCH":
      return ta("The branch does not belong to this customer.");
    case "BRANCH_ARCHIVED":
      return ta("This branch is archived. Restore it on the customer page to use it again.");
    default:
      return details && typeof details === "string" ? details : message;
  }
};

/** One line of the CREDIT_EXCEEDS_INVOICE details. */
export const creditLimitText = (ta, e) => {
  switch (e?.code) {
    case "QUANTITY":
      return ta("Line {{line}}: only {{remaining}} left to credit", {
        line: Number(e.index) + 1,
        remaining: e.remaining
      });
    case "RATE":
      return ta("{{rate}} % VAT: only {{amount}} net left to credit", {
        rate: e.rate,
        amount: formatCents(e.remaining)
      });
    case "TOTAL":
      return ta("The total exceeds the remaining {{amount}}", {
        amount: formatCents(e.remaining)
      });
    case "RATE_NOT_ON_INVOICE":
      return ta("This VAT rate does not occur on the invoice");
    case "UNKNOWN_LINE":
      return ta("Line {{line}} does not exist", { line: Number(e.index) + 1 });
    default:
      return e?.message || "";
  }
};

/** Translated text of a Pflichtangaben finding { field, message, severity }. */
export const preflightText = (ta, f) => {
  const field = String(f?.field || "");
  const line = /^items\.(\d+)\./.exec(field);
  if (line && field.endsWith(".description"))
    return ta("Line {{line}} has no description", { line: Number(line[1]) + 1 });
  if (line && field.endsWith(".quantity"))
    return ta("Line {{line}} has no quantity", { line: Number(line[1]) + 1 });
  const msg = String(f?.message || "");
  switch (field) {
    case "smallAmountInvoice":
      return ta(
        "A small-amount invoice is only possible for an ordinary invoice up to 250 € gross"
      );
    case "seller.name":
      return ta("Your company name is missing (settings / profile)");
    case "seller.address":
      return ta("Your company address is incomplete (street, ZIP, city)");
    case "seller.taxNumber":
      return ta("Your tax number or VAT ID is missing");
    case "seller.vatId":
      return ta("Your own VAT ID is required for an EU invoice without German VAT");
    case "seller.glaeubigerId":
      return ta("Your SEPA creditor ID is missing (direct debit)");
    case "customer.name":
      return ta("The customer's name is missing");
    case "customer.address": {
      const branch = /branch "(.+)"/.exec(msg);
      return branch
        ? ta("The address of the branch {{name}} is incomplete (street, ZIP, city)", {
            name: branch[1]
          })
        : ta("The customer's address is incomplete (street, ZIP, city)");
    }
    case "customer.branch":
      return ta("The branch of this invoice no longer exists");
    case "customer.vatId":
      return /required/i.test(msg)
        ? ta("The customer's VAT ID is required for an EU invoice without German VAT")
        : ta("The customer's VAT ID looks invalid");
    case "customer.taxExemptionCertificate":
      return /^No /.test(msg)
        ? ta("No USt 1 TG certificate of the customer is on file (§ 13b Abs. 5 UStG)")
        : ta("The customer's USt 1 TG certificate has expired");
    case "customer.leitwegId":
      return ta("Public clients need a Leitweg-ID for the XRechnung");
    case "customer.sepaMandate":
      return ta("The customer has no SEPA mandate (reference and IBAN)");
    case "customer.insolvency":
      return ta("The customer is insolvent");
    case "items":
      return ta("The invoice has no lines");
    case "servicePeriod":
      return ta("The service period ends before it starts");
    case "correctionReason":
      return ta("An invoice correction needs a reason.");
    default:
      return msg;
  }
};
